import React, { useEffect, useState } from 'react';
import { Segment, Progress, Icon, Modal } from 'semantic-ui-react';
import { PieChart, Pie, Cell, Tooltip, LineChart, Line, XAxis, YAxis, ResponsiveContainer } from 'recharts';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useStore } from '../../app/stores/store';

interface Props {
  userId: string;
  isOwnProfile: boolean;
}

type HistoryRange = 7 | 30 | 90;

const BADGE_COLORS: Record<string, string> = {
  '🟤': '#92400e',
  '🟢': '#16a34a',
  '🔵': '#2563eb',
  '🟣': '#7c3aed',
  '🟡': '#ca8a04',
};

const LEVELS = [
  { badge: '🟤', key: 'playground.influence.levels.novice', range: '0–299', km: 3 },
  { badge: '🟢', key: 'playground.influence.levels.rookie', range: '300–799', km: 7 },
  { badge: '🔵', key: 'playground.influence.levels.player', range: '800–1 999', km: 15 },
  { badge: '🟣', key: 'playground.influence.levels.captain', range: '2 000–4 999', km: 30 },
  { badge: '🟡', key: 'playground.influence.levels.legend', range: '5 000+', km: 50 },
];

export const InfluenceWidget = observer(({ userId, isOwnProfile }: Props) => {
  const { playgroundStore } = useStore();
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(true);
  const [historyDays, setHistoryDays] = useState<HistoryRange>(30);
  const [infoOpen, setInfoOpen] = useState(false);

  useEffect(() => {
    playgroundStore.loadInfluenceProfile(userId, historyDays);
  }, [userId, historyDays]);

  const profile = playgroundStore.influenceProfile;
  if (!profile) return null;

  const levelName = profile.levelKey ? t(profile.levelKey) : profile.level;

  const progressPct = profile.pointsToNextLevel === 0
    ? 100
    : Math.round(
        ((profile.score - (profile.nextLevelScore - profile.pointsToNextLevel - profile.score)) /
          profile.nextLevelScore) * 100
      );

  const pieData = isOwnProfile
    ? [
        { name: t('playground.influence.likes'), value: profile.totalLikes, color: '#3b82f6' },
        { name: t('playground.influence.notRelevant'), value: profile.totalNotRelevant, color: '#ef4444' },
      ].filter(d => d.value > 0)
    : [{ name: t('playground.influence.likes'), value: profile.totalLikes, color: '#3b82f6' }];

  const earnActions = [
    { icon: '👍', label: t('playground.influence.action.postLike'),     pts: '+10' },
    { icon: '💬', label: t('playground.influence.action.comment'),      pts: '+5'  },
    { icon: '⚽', label: t('playground.influence.action.joined'),       pts: '+15' },
    { icon: '🔁', label: t('playground.influence.action.shared'),       pts: '+3'  },
    { icon: '❤️', label: t('playground.influence.action.commentLike'),  pts: '+3'  },
    { icon: '➕', label: t('playground.influence.action.follow'),       pts: '+8'  },
    { icon: '🤝', label: t('playground.influence.action.friend'),       pts: '+20' },
    { icon: '🏆', label: t('playground.influence.action.tournamentCreated'), pts: '+50' },
    { icon: '🎁', label: t('playground.influence.action.starter'),      pts: '+100'},
  ];

  const loseActions = [
    { icon: '👎', label: t('playground.influence.action.notRelevant'),  pts: '−5'   },
    { icon: '➖', label: t('playground.influence.action.unfollow'),     pts: '−8'   },
    { icon: '💔', label: t('playground.influence.action.friendEnded'),  pts: '−10'  },
    { icon: '🗑️', label: t('playground.influence.action.postDeleted'), pts: '−pts' },
    { icon: '🏆', label: t('playground.influence.action.tournamentDeleted'), pts: '−50' },
    { icon: '⏳', label: t('playground.influence.action.decay'),        pts: '−2%'  },
  ];

  return (
    <>
      {/* ── How It Works Modal ── */}
      <Modal open={infoOpen} onClose={() => setInfoOpen(false)} size="small" closeIcon>
        <Modal.Header>
          ⚡ {t('playground.influence.howItWorksTitle')}
        </Modal.Header>
        <Modal.Content scrolling>

          {/* Levels */}
          <div style={{ marginBottom: 20 }}>
            <SectionLabel color="#555" text={t('playground.influence.levelsTitle')} />
            {LEVELS.map(({ badge, key, range, km }) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: '0.88em' }}>
                <span style={{ fontSize: '1.1em', width: 22 }}>{badge}</span>
                <span style={{ fontWeight: 600, minWidth: 72 }}>{t(key)}</span>
                <span style={{ color: '#888', flex: 1 }}>{range} pts</span>
                <span style={{ color: '#555', fontWeight: 600 }}>📍 {km} km</span>
              </div>
            ))}
          </div>

          <Divider />

          {/* Earn */}
          <div style={{ marginBottom: 20 }}>
            <SectionLabel color="#16a34a" text={t('playground.influence.earnTitle')} />
            {earnActions.map(({ icon, label, pts }) => (
              <ActionRow key={label} icon={icon} label={label} pts={pts} ptsColor="#16a34a" />
            ))}
          </div>

          <Divider />

          {/* Lose */}
          <div>
            <SectionLabel color="#dc2626" text={t('playground.influence.loseTitle')} />
            {loseActions.map(({ icon, label, pts }) => (
              <ActionRow key={label} icon={icon} label={label} pts={pts} ptsColor="#dc2626" />
            ))}
          </div>

        </Modal.Content>
      </Modal>

      {/* ── Widget ── */}
      <Segment style={{
        borderRadius: 10,
        overflow: 'hidden',
        padding: '8px 10px',
        marginBottom: 0,
        border: '1px solid #e8eaf6',
        boxShadow: 'none',
      }}>
        {/* Collapsed header */}
        <div
          onClick={() => setCollapsed(!collapsed)}
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', gap: 8 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: '1.3em', flexShrink: 0 }}>{profile.badge}</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '0.85em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {levelName}
              </div>
              <div style={{ fontSize: '0.72em', color: '#888', whiteSpace: 'nowrap' }}>
                {t('playground.influence.reach', { km: profile.reachKm })}
              </div>
            </div>
            <div style={{
              fontWeight: 800,
              fontSize: '1.1em',
              color: BADGE_COLORS[profile.badge] || '#333',
              flexShrink: 0,
              marginLeft: 'auto',
            }}>
              {profile.score.toLocaleString()}
            </div>
          </div>
          <Icon name={collapsed ? 'chevron down' : 'chevron up'} style={{ color: '#aaa', flexShrink: 0, margin: 0 }} />
        </div>

        {/* Expanded content */}
        {!collapsed && (
          <div style={{ marginTop: 10 }}>

            {/* Progress bar */}
            {profile.pointsToNextLevel > 0 ? (
              <div style={{ marginBottom: 12 }}>
                <Progress percent={Math.min(100, progressPct)} size="tiny" color="blue" style={{ marginBottom: 2 }} />
                <div style={{ fontSize: '0.72em', color: '#888' }}>
                  {t('playground.influence.pointsToNext', { points: profile.pointsToNextLevel })}
                </div>
              </div>
            ) : (
              <div style={{ color: '#ca8a04', fontWeight: 600, fontSize: '0.82em', marginBottom: 12 }}>
                ✨ {t('playground.influence.maxLevel')}
              </div>
            )}

            {/* Stats row */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: 700, fontSize: '1em' }}>👍 {profile.totalLikes}</div>
                <div style={{ fontSize: '0.7em', color: '#888' }}>{t('playground.influence.likes')}</div>
              </div>
              {isOwnProfile && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontWeight: 700, fontSize: '1em' }}>👎 {profile.totalNotRelevant}</div>
                  <div style={{ fontSize: '0.7em', color: '#888' }}>{t('playground.influence.notRelevant')}</div>
                </div>
              )}
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: 700, fontSize: '1em' }}>📝 {profile.totalPostsAndGames}</div>
                <div style={{ fontSize: '0.7em', color: '#888' }}>{t('playground.influence.postsGames')}</div>
              </div>

              {/* Info button — opens modal */}
              <div style={{ marginLeft: 'auto' }}>
                <Icon
                  name="info circle"
                  style={{ color: '#aaa', cursor: 'pointer' }}
                  onClick={(e: React.MouseEvent) => { e.stopPropagation(); setInfoOpen(true); }}
                />
              </div>
            </div>

            {/* Pie chart */}
            {pieData.length > 0 && profile.totalLikes > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: '0.78em', fontWeight: 600, marginBottom: 4, color: '#555' }}>
                  {t('playground.influence.engagement')}
                </div>
                <ResponsiveContainer width="100%" height={90}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" cx="50%" cy="50%" outerRadius={36} innerRadius={18}>
                      {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Line chart */}
            {isOwnProfile && profile.history.length > 1 && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <div style={{ fontSize: '0.78em', fontWeight: 600, color: '#555' }}>
                    {t('playground.influence.history')}
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {([7, 30, 90] as HistoryRange[]).map(d => (
                      <button
                        key={d}
                        onClick={(e) => { e.stopPropagation(); setHistoryDays(d); }}
                        style={{
                          background: historyDays === d ? '#2563eb' : '#f0f0f0',
                          color: historyDays === d ? '#fff' : '#555',
                          border: 'none',
                          borderRadius: 10,
                          padding: '2px 6px',
                          fontSize: '0.7em',
                          cursor: 'pointer',
                          fontWeight: 600,
                        }}
                      >
                        {d}d
                      </button>
                    ))}
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={90}>
                  <LineChart data={profile.history.map(h => ({ ...h, label: format(h.date, 'MMM d') }))}>
                    <XAxis dataKey="label" tick={{ fontSize: 8 }} />
                    <YAxis tick={{ fontSize: 8 }} width={30} />
                    <Tooltip formatter={(v: number) => [v, 'Score']} />
                    <Line type="monotone" dataKey="score" stroke="#2563eb" dot={false} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

          </div>
        )}
      </Segment>
    </>
  );
});

// ── Small helpers ──────────────────────────────────────────────────────────────

const SectionLabel = ({ color, text }: { color: string; text: string }) => (
  <div style={{
    fontWeight: 700,
    fontSize: '0.78em',
    color,
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 1,
  }}>
    {text}
  </div>
);

const ActionRow = ({ icon, label, pts, ptsColor }: { icon: string; label: string; pts: string; ptsColor: string }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, fontSize: '0.87em' }}>
    <span style={{ width: 20 }}>{icon}</span>
    <span style={{ flex: 1, color: '#444' }}>{label}</span>
    <span style={{ fontWeight: 700, color: ptsColor, minWidth: 36, textAlign: 'right' }}>{pts}</span>
  </div>
);

const Divider = () => (
  <div style={{ borderTop: '1px solid #eee', margin: '0 0 16px' }} />
);